// DATE-ONLY values (Postgres `DATE`: due dates, phase release dates, exclusivity
// expiry, closing date) are CALENDAR dates — no time, no zone. Parsing one with
// `new Date('2026-10-15')` reads it as UTC midnight, which in every US time zone
// is the previous evening, so it displayed (and compared) a day early. These
// helpers treat a date-only value as a LOCAL calendar date end to end.
//
// Input tolerance: the API returns these either as plain 'YYYY-MM-DD' or (until
// the server change lands) as a full ISO timestamp of that date's midnight, e.g.
// '2026-10-15T00:00:00.000Z'. Both are accepted; only the leading date part is
// used, so the value never shifts with the viewer's zone.

const YMD_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** The 'YYYY-MM-DD' calendar date of a date-only value, or null when absent/unparseable. */
export function toYmd(value: string | null | undefined): string | null {
  const m = value ? YMD_PREFIX.exec(value) : null;
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/** A date-only value as a Date at LOCAL midnight of that calendar day, or null. */
export function parseDateOnly(value: string | null | undefined): Date | null {
  const ymd = toYmd(value);
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
  const date = new Date(y, m - 1, d);
  // Reject roll-overs like 2026-02-31.
  return date.getMonth() === m - 1 && date.getDate() === d ? date : null;
}

/**
 * THE app date format (DataRoom UI): "Oct 2, 2026" (MMM D, YYYY) everywhere —
 * never 10/2/2026 or 2026-10-02. Times are "10:42 AM". Fixed to en-US so every
 * viewer sees the same shape the copy and screenshots use.
 */
const DATE_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
const TIME_FORMAT: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
const LOCALE = 'en-US';

/** Display a date-only value as "Oct 15, 2026" (or `options`), else `fallback`. */
export function formatDateOnly(
  value: string | null | undefined,
  fallback = '—',
  options: Intl.DateTimeFormatOptions = DATE_FORMAT,
): string {
  const date = parseDateOnly(value);
  return date ? date.toLocaleDateString(LOCALE, options) : fallback;
}

function toInstant(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** An INSTANT (ISO timestamp: createdAt, certifiedAt, …) as its LOCAL calendar
 * date, "Oct 2, 2026"; `fallback` when absent/unparseable. */
export function formatDate(value: string | Date | null | undefined, fallback = '—'): string {
  const d = toInstant(value);
  return d ? d.toLocaleDateString(LOCALE, DATE_FORMAT) : fallback;
}

/** An instant's local time of day, "10:42 AM". */
export function formatTime(value: string | Date | null | undefined, fallback = '—'): string {
  const d = toInstant(value);
  return d ? d.toLocaleTimeString(LOCALE, TIME_FORMAT) : fallback;
}

/** An instant as "Oct 2, 2026, 10:42 AM". */
export function formatDateTime(value: string | Date | null | undefined, fallback = '—'): string {
  const d = toInstant(value);
  return d ? `${formatDate(d)}, ${formatTime(d)}` : fallback;
}

/** Today's LOCAL calendar date as 'YYYY-MM-DD' (not UTC's). */
export function todayYmd(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** `ymd` + `days` calendar days, as 'YYYY-MM-DD' (DST-safe: local date arithmetic). */
export function addDaysYmd(ymd: string, days: number): string {
  const date = parseDateOnly(ymd);
  if (!date) throw new Error(`Not a calendar date: ${ymd}`);
  date.setDate(date.getDate() + days);
  return todayYmd(date);
}

/**
 * The INSTANT a "valid through <date>" choice ends: 23:59:59.999 at the end of
 * that calendar day in the viewer's LOCAL time zone, as an ISO timestamp. Used
 * for access-grant expiry, where the API stores a real instant (`expiresAt`,
 * `timestamptz`) and access lasts while `expires_at > now()`: picking Oct 15
 * means access through the end of Oct 15 where the admin is, not 7 pm
 * the evening before (which UTC end-of-day was in US zones).
 */
export function endOfLocalDayIso(ymd: string): string {
  const date = parseDateOnly(ymd);
  if (!date) throw new Error(`Not a calendar date: ${ymd}`);
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
}
